import { useGlobalUserMappings } from "../hooks/useGlobalHooks";
import { LiveLTPCell } from "./OptimizedCell";
import BulkModifyModal from "./BulkModifyModal";
import { Pencil } from "lucide-react";
import { toast } from "react-hot-toast";
import { useGlobalData } from "../hooks/useGlobalHooks";
import React, { useMemo, useState } from "react";
// unused: import apiClient from "../utils/apiClient";

const formatPrice = (price) => {
    if (price === undefined || price === null || price === 0) return "-";
    return price.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Commodity Comparison Modal
 * Compares actual client positions with reference base quantities
 */
export function CommodityComparisonModal({
    isOpen,
    onClose,
    clientData, // { clientId, clientName, ... }
    positions,  // Array of all positions (must be passed from parent or store)
    orders,     // Array of all orders
    symbolStrategyBaseQty,
    onCancel,
}) {
    const { userMappings } = useGlobalUserMappings();

    // Selection & Modify State
    const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
    const [modifyModalOpen, setModifyModalOpen] = useState(false);
    const [ordersToModify, setOrdersToModify] = useState([]);

    // Open Confirmation Modal via Parent
    const openCancelModal = (orders) => {
        if (onCancel) {
            onCancel(orders);
        }
    };

    // Helper: Extract commodity (Same as in hook, duplicated for safety/isolation)
    // Helper: Extract commodity (Same as in hook, duplicated for safety/isolation)
    function getCommodityFromSymbol(symbol) {
        const s = symbol ? symbol.toUpperCase() : "";
        const isFUT = s.endsWith("FUT");
        if (!s.includes("MCX") && !s.includes("NFO") && !s.includes("BFO") && !isFUT) return null;

        const cleanSymbol = s.replace("MCX:", "").replace("NFO:", "").replace("BFO:", "").replace("NSE:", "").replace("BSE:", "");
        const commodities = [
            "CRUDEOIL", "GOLD", "SILVER", "NATURALGAS",
            "COPPER", "ZINC", "LEAD", "ALUMINIUM", "MENTHAOIL", "COTTON"
        ];
        for (const comm of commodities) {
            if (cleanSymbol.startsWith(comm)) return comm;
        }
        const match = cleanSymbol.match(/^([A-Z]+)/);
        return match ? match[1] : null;
    }

    // Get multiplier using User Mappings (same logic as useCommoditiesView)
    function getMultiplier(clientId, commodity) {
        if (!userMappings || !clientId || !commodity) return 1;
        const mapping = userMappings.find((u) => u.client_id === clientId);
        if (!mapping) return 1;
        const key = `${commodity.toLowerCase()}_multiplier`;
        const val = Number(mapping[key]);
        return val > 0 ? val : 1;
    }

    // Format time (HH:MM:SS)
    function formatTime(isoString) {
        if (!isoString) return "-";
        try {
            const date = new Date(isoString);
            return date.toLocaleTimeString('en-IN', { hour12: false });
        } catch (e) {
            return isoString;
        }
    }

    // Build rows for the modal table
    const comparisonRows = useMemo(() => {
        if (!positions || !clientData?.clientId || !symbolStrategyBaseQty) return [];

        console.log("Comaparing for Client:", clientData.clientId);
        console.log("Total Orders Available:", orders ? orders.length : "NULL");

        const rows = [];
        const processedKeys = new Set();
        const matchedOrderIds = new Set();
        const clientId = clientData.clientId;

        // Filter for this client
        const clientPositions = positions.filter(p => p.client_id === clientId);

        clientPositions.forEach((pos) => {
            const commodity = getCommodityFromSymbol(pos.symbol);
            if (!commodity) return;

            // Filter out N/A strategies
            if (!pos.strategy || pos.strategy === "N/A" || pos.strategy === "N") return;

            const key = `${pos.symbol}_${pos.strategy || "NO_STRATEGY"}`;
            if (processedKeys.has(key)) return;
            processedKeys.add(key);

            const baseQtyInfo = symbolStrategyBaseQty[key];
            if (!baseQtyInfo) return;

            const multiplier = getMultiplier(clientId, commodity);
            const baseQty = baseQtyInfo.baseQty;
            const expectedQty = multiplier * baseQty;

            let actualQty = 0;
            let netSignedQty = 0;
            // Debugging array to store contributing positions
            const contributingPositions = [];
            clientPositions.forEach((p) => {
                const pKey = `${p.symbol}_${p.strategy || "NO_STRATEGY"}`;
                if (pKey === key) {
                    const qty = Math.abs(parseFloat(p.net_qty || p.qty || 0));
                    const signedQ = parseFloat(p.net_qty || p.qty || 0);
                    actualQty += qty;
                    netSignedQty += signedQ;
                    contributingPositions.push({ ...p, qtyUsed: qty });
                }
            });

            // Determine expected match side based on netSignedQty
            // netSignedQty > 0 => Long Position => Expect SELL orders
            // netSignedQty < 0 => Short Position => Expect BUY orders
            // netSignedQty === 0 => Flat Position => No SL expected
            const expectedOrderSide = netSignedQty > 0 ? "sell" : (netSignedQty < 0 ? "buy" : null);

            // Debug specific mismatch for user
            if (pos.symbol.includes("CRUDEOIL") && pos.strategy === "PDI_MDI" && actualQty !== expectedQty) {
                console.log(`[DEBUG MISMATCH] ${pos.symbol} ${pos.strategy}:`);
                console.log("Expected:", expectedQty, "Actual:", actualQty);
                console.log("Contributing Positions:", contributingPositions);
            }

            // Find matching Pending Orders (ALL matches)
            let matchingOrders = [];
            if (orders) {
                matchingOrders = orders.filter(o => {
                    const oClientId = o.clientId || o.client_id;
                    const oSymbol = o.symbol;
                    const oStrategy = o.strategy;
                    const oSide = String(o.side || "").toLowerCase();
                    const oStatus = (o.status || "").toLowerCase();
                    const oStatusNo = o.status_no !== undefined ? parseInt(o.status_no, 10) : undefined;

                    // console.log("Scanning order:", o.order_id, o.client_id, o.symbol); // FORCE LOG

                    // Client, Symbol, Strategy Match

                    const currentClientId = (oClientId || "").toString().trim();
                    const targetClientId = (clientId || "").toString().trim();
                    if (currentClientId !== targetClientId) return false;

                    // Normalize symbols (handle MCX, NFO, BFO, NSE, BSE prefixes)
                    const cleanPosSymbol = (pos.symbol || "").replace(/^(MCX|NFO|BFO|NSE|BSE):/i, "").trim();
                    const cleanOrderSymbol = (oSymbol || "").replace(/^(MCX|NFO|BFO|NSE|BSE):/i, "").trim();

                    // Strategy Match (Case-insensitive)
                    const currentStrategy = (oStrategy || "").toString().trim().toUpperCase();
                    const targetStrategy = (pos.strategy || "").toString().trim().toUpperCase();

                    if (cleanPosSymbol !== cleanOrderSymbol) return false;
                    if (currentStrategy !== targetStrategy) return false;

                    // Side Match
                    const isSell = oSide === "-1" || oSide === "2" || oSide === "sell";
                    const isBuy = oSide === "1" || oSide === "buy" || oSide === "b";

                    if (expectedOrderSide === "sell" && !isSell) return false;
                    if (expectedOrderSide === "buy" && !isBuy) return false;

                    // Status Match (Strict: Pending AND 6)
                    const isPending = (oStatus === "pending" && oStatusNo === 6) || oStatus === "pending";
                    return isPending;
                });
            }

            // Track matched order IDs
            matchingOrders.forEach(o => matchedOrderIds.add(o.order_id || o.orderId));

            // Calculate total SL Qty from all matching orders
            const totalSlQty = matchingOrders.reduce((acc, o) => acc + (parseInt(o.qty) || 0), 0);

            // Determine match status based on TOTAL SL Qty
            const isMatch = Math.abs(totalSlQty) === Math.abs(expectedQty);

            // Filter Empty Rows: If Position is Flat AND No SL Orders, don't show the row.
            // This prevents "S1 0 0" rows from cluttering the view.
            if (Math.abs(actualQty) === 0 && matchingOrders.length === 0) return;

            rows.push({
                index: commodity,
                symbol: pos.symbol,
                strategy: pos.strategy || "N/A",
                baseQty,
                multiplier,
                expectedQty,
                actualQty: actualQty, // Position Qty
                netSignedQty: netSignedQty,
                buyPrice: pos.net_avg || pos.avg_price || 0,
                match: isMatch,
                matchingOrders: matchingOrders, // Array of orders
                totalSlQty: totalSlQty
            });
        });

        // --- ORPHAN HANDLING REMOVED ---
        // Orphans (Orders with no Position) are Entry Orders, not SLs.
        // We should not display them in the "SL Comparison" view as they confuse the user.
        // (Previously lines 174-220)

        return rows;
    }, [positions, clientData, symbolStrategyBaseQty, userMappings, orders]);

    // -- FILTER LOGIC --
    const [selectedAsset, setSelectedAsset] = useState("ALL");

    const uniqueAssets = useMemo(() => {
        const assets = new Set(comparisonRows.map(r => r.index).filter(Boolean));
        return ["ALL", ...Array.from(assets).sort()];
    }, [comparisonRows]);

    const filteredRows = useMemo(() => {
        if (selectedAsset === "ALL") return comparisonRows;
        return comparisonRows.filter(r => r.index === selectedAsset);
    }, [comparisonRows, selectedAsset]);

    // Modify Handlers
    const openModifyModal = (orderList) => {
        // Find row to get expected Qty
        const enriched = orderList.map(o => {
            const row = comparisonRows.find(r => r.matchingOrders?.some(mo => (mo.order_id || mo.orderId) === (o.order_id || o.orderId)));
            return {
                ...o,
                expectedQty: row?.expectedQty
            };
        });
        setOrdersToModify(enriched);
        setModifyModalOpen(true);
    };

    const toggleOrderSelection = (orderId) => {
        const next = new Set(selectedOrderIds);
        if (next.has(orderId)) next.delete(orderId);
        else next.add(orderId);
        setSelectedOrderIds(next);
    };

    const handleBulkModify = () => {
        const selectedOrders = [];
        comparisonRows.forEach(row => {
            if (row.matchingOrders) {
                row.matchingOrders.forEach(o => {
                    if (selectedOrderIds.has(o.order_id || o.orderId)) {
                        selectedOrders.push(o);
                    }
                });
            }
        });

        if (selectedOrders.length === 0) {
            toast.error("Please select at least one order to modify");
            return;
        }
        openModifyModal(selectedOrders);
    };

    if (!isOpen) return null;

    return (
        <>
            <div
                className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                onClick={onClose}
            >
                <div
                    className="bg-[#1a1d29] rounded-lg shadow-xl max-w-7xl w-full max-h-[80vh] overflow-hidden flex flex-col border border-gray-700/50"
                    onClick={(e) => e.stopPropagation()}
                >
                {/* Header */}
                <div className="bg-[#14161f] px-6 py-4 border-b border-gray-700 flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <h2 className="text-xl font-bold text-white">Commodity Position Comparison</h2>
                        {selectedOrderIds.size > 0 && (
                            <button
                                onClick={handleBulkModify}
                                className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold px-3 py-1.5 rounded transition-all shadow-lg flex items-center gap-2"
                            >
                                Modify Selected ({selectedOrderIds.size})
                            </button>
                        )}
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Client Info */}
                <div className="bg-[#1a1d29] px-6 py-3 border-b border-gray-700/50">
                    <div className="flex items-center gap-4 text-sm">
                        <span className="text-gray-400">Client:</span>
                        <span className="text-white font-semibold">
                            {clientData?.clientName || clientData?.clientId}
                        </span>
                        <span className="text-gray-400">({clientData?.clientId})</span>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto">
                    {comparisonRows.length === 0 ? (
                        <div className="text-center py-8 px-6">
                            <p className="text-gray-400">No commodity positions found for comparison</p>
                        </div>
                    ) : (
                        <div className="bg-[#14161f] overflow-hidden">
                            <div className="overflow-auto max-h-[calc(80vh-280px)]">
                                <table className="w-full text-sm">
                                    <thead className="bg-[#0f1116] sticky top-0 z-10">
                                        {/* Group Header Row */}
                                        <tr className="border-b border-gray-700/50">
                                            <th colSpan="10" className="text-center py-2 bg-[#14161f] text-gray-400 font-semibold text-xs uppercase tracking-wider border-r border-gray-700/50">
                                                Position Comparison
                                            </th>
                                            <th colSpan="7" className="text-center py-2 bg-gray-900/40 text-gray-400 font-semibold text-xs uppercase tracking-wider">
                                                Matching SL Orders (Opposite Side)
                                            </th>
                                        </tr>
                                        {/* Column Header Row */}
                                        <tr className="border-b border-gray-700/30">
                                            <th className="w-8 px-2 text-center bg-[#0f1116]">
                                                <input 
                                                    type="checkbox"
                                                    className="w-4 h-4 rounded border-gray-700 bg-gray-900 text-blue-600 focus:ring-blue-500"
                                                    checked={filteredRows.some(r => r.matchingOrders?.length > 0) && filteredRows.every(r => !r.matchingOrders || r.matchingOrders.every(o => selectedOrderIds.has(o.order_id || o.orderId)))}
                                                    onChange={(e) => {
                                                        const allIds = new Set(selectedOrderIds);
                                                        filteredRows.forEach(r => {
                                                            if (r.matchingOrders) {
                                                                r.matchingOrders.forEach(o => {
                                                                    if (e.target.checked) allIds.add(o.order_id || o.orderId);
                                                                    else allIds.delete(o.order_id || o.orderId);
                                                                });
                                                            }
                                                        });
                                                        setSelectedOrderIds(allIds);
                                                    }}
                                                />
                                            </th>
                                            <th className="text-left py-3 px-4 text-gray-400 font-medium bg-[#0f1116]">
                                                <div className="flex items-center gap-2">
                                                    <span>Commodity</span>
                                                    <select
                                                        value={selectedAsset}
                                                        onChange={(e) => setSelectedAsset(e.target.value)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="bg-[#1a1d29] border border-gray-700 text-gray-300 text-xs rounded px-1 py-0.5 focus:outline-none focus:border-blue-500"
                                                    >
                                                        {uniqueAssets.map(asset => (
                                                            <option key={asset} value={asset}>
                                                                {asset === "ALL" ? "All" : asset.toUpperCase()}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </th>
                                            <th className="text-left py-3 px-4 text-gray-400 font-medium bg-[#0f1116]">Symbol</th>
                                            <th className="text-left py-3 px-4 text-gray-400 font-medium bg-[#0f1116]">Strategy</th>
                                            <th className="text-center py-3 px-4 text-gray-400 font-medium bg-[#0f1116]">Base Qty (1x)</th>
                                            <th className="text-center py-3 px-4 text-gray-400 font-medium bg-[#0f1116]">Multiplier</th>
                                            <th className="text-center py-3 px-4 text-gray-400 font-medium bg-[#0f1116]">Expected Qty</th>
                                            <th className="text-center py-3 px-4 text-gray-400 font-medium bg-[#0f1116]">Actual Qty</th>
                                            <th className="text-center py-3 px-4 text-gray-400 font-medium bg-[#0f1116]">Buy Price</th>
                                            <th className="text-center py-3 px-4 text-gray-400 font-medium bg-[#0f1116]">LTP</th>
                                            <th className="text-center py-3 px-4 text-gray-400 font-medium bg-[#0f1116] border-r border-gray-700/50">Match</th>
                                            <th className="text-center py-3 px-4 text-red-300 font-medium bg-red-900/5 border-l border-gray-700/30">Order ID</th>
                                            <th className="text-center py-3 px-4 text-red-300 font-medium bg-red-900/5 border-l border-gray-700/30">Time</th>
                                            <th className="text-center py-3 px-4 text-red-300 font-medium bg-red-900/5 border-l border-gray-700/30">Order Qty</th>
                                            <th className="text-center py-3 px-4 text-red-300 font-medium bg-red-900/5 border-l border-gray-700/30">Stop Loss</th>
                                            <th className="text-center py-3 px-4 text-red-300 font-medium bg-red-900/5 border-l border-gray-700/30">Limit Price</th>
                                            <th className="text-center py-3 px-4 text-red-300 font-medium bg-red-900/5 border-l border-gray-700/30">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredRows.map((row, idx) => {
                                            const isMatch = row.match;
                                            const qtyColor = isMatch ? "text-green-400" : "text-red-400";
                                            const hasOrders = row.matchingOrders && row.matchingOrders.length > 0;

                                            // Determine Row Direction for styling
                                            const rowDirection = row.netSignedQty > 0 ? "LONG" : (row.netSignedQty < 0 ? "SHORT" : "FLAT");

                                            // Dynamic Header for matching SL orders based on position side
                                            // If LONG position -> Selling matches -> "Matching SL (SELL)"
                                            // If SHORT position -> Buying matches -> "Matching SL (BUY)"
                                            const matchSideText = rowDirection === "LONG" ? "(SELL)" : (rowDirection === "SHORT" ? "(BUY)" : "");

                                            return (
                                                <tr key={idx} className={`border-b border-gray-800/30 transition-colors valign-top ${rowDirection === "LONG" ? "bg-green-500/5 hover:bg-green-500/10" : (rowDirection === "SHORT" ? "bg-red-500/5 hover:bg-red-500/10" : "hover:bg-[#1a1d29]")}`}>
                                                    <td className="px-2 text-center align-middle">
                                                        {hasOrders && (
                                                            <div className="flex flex-col gap-1 items-center">
                                                                {row.matchingOrders.map((o, i) => (
                                                                    <div key={i} className="h-[20px] flex items-center">
                                                                        <input 
                                                                            type="checkbox"
                                                                            className="w-3.5 h-3.5 rounded border-gray-800 bg-gray-950 text-blue-600 focus:ring-blue-500"
                                                                            checked={selectedOrderIds.has(o.order_id || o.orderId)}
                                                                            onChange={() => toggleOrderSelection(o.order_id || o.orderId)}
                                                                        />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className={`py-2.5 px-4 border-l-4 ${rowDirection === "LONG" ? "border-green-500" : (rowDirection === "SHORT" ? "border-red-500" : "border-transparent")}`}>
                                                        <span className="text-[10px] bg-red-800/50 text-red-200 px-1 py-0.5 rounded">{row.index}</span>
                                                    </td>
                                                    <td className="py-2.5 px-4 text-gray-300 text-xs font-mono">{row.symbol}</td>
                                                    <td className="py-2.5 px-4 text-gray-400 text-xs">{row.strategy}</td>
                                                    <td className="py-2.5 px-4 text-center text-white font-medium">{row.baseQty}</td>
                                                    <td className="py-2.5 px-4 text-center text-purple-400 font-medium">{row.multiplier}x</td>
                                                    <td className="py-2.5 px-4 text-center text-yellow-400 font-medium">{row.expectedQty}</td>
                                                    <td className={`py-2.5 px-4 text-center font-bold ${qtyColor}`}>
                                                        <div className="flex items-center justify-center gap-1.5">
                                                            <span className="text-lg leading-none">{row.actualQty}</span>
                                                            {row.netSignedQty !== undefined && row.netSignedQty !== 0 && (
                                                                <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold tracking-wider ${row.netSignedQty > 0 ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
                                                                    {row.netSignedQty > 0 ? "L" : "S"}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="py-2.5 px-4 text-center text-gray-300 font-medium">
                                                        {formatPrice(row.buyPrice)}
                                                    </td>
                                                    <td className="py-2.5 px-4 text-center text-blue-400 font-medium">
                                                        <LiveLTPCell symbol={row.symbol} formatFn={formatPrice} />
                                                    </td>
                                                    <td className="py-2.5 px-4 text-center">
                                                        {isMatch ? <span className="text-green-400">✓</span> : <span className="text-red-400">✗</span>}
                                                    </td>

                                                    {/* Order Details (Mapped) */}
                                                    <td className="py-2.5 px-4 text-center text-gray-300 text-xs font-mono border-l border-gray-700/50 align-top">
                                                        {hasOrders ? (
                                                            <div className="flex flex-col gap-1">
                                                                {row.matchingOrders.map((o, i) => (
                                                                    <div key={i} className="whitespace-nowrap">
                                                                        {o.order_id || o.orderId || "N/A"}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-600">-</span>
                                                        )}
                                                    </td>
                                                    <td className="py-2.5 px-4 text-center text-gray-300 text-xs font-mono border-l border-gray-700/30 align-top">
                                                        {hasOrders ? (
                                                            <div className="flex flex-col gap-1">
                                                                {row.matchingOrders.map((o, i) => (
                                                                    <div key={i} className="text-gray-400 text-[10px]">
                                                                        {formatTime(o.orderdatetime || o.order_datetime || o.orderDateTime || o.orderTime)}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-600">-</span>
                                                        )}
                                                    </td>
                                                    <td className="py-2.5 px-4 text-center text-gray-300 font-medium border-l border-gray-700/30 align-top">
                                                        {hasOrders ? (
                                                            <div className="flex flex-col gap-1">
                                                                {row.matchingOrders.map((o, i) => {
                                                                    const side = String(o.side || "").toLowerCase();
                                                                    const isBuy = side === "1" || side === "buy" || side === "b";
                                                                    return (
                                                                        <div key={i} className="flex items-center justify-center gap-1">
                                                                            <span className={isBuy ? "text-green-300" : "text-red-300"}>
                                                                                {o.qty}
                                                                            </span>
                                                                            <span className={`text-[9px] px-1 rounded font-bold ${isBuy ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
                                                                                {isBuy ? "B" : "S"}
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-600">-</span>
                                                        )}
                                                    </td>
                                                    <td className="py-2.5 px-4 text-center text-gray-300 font-medium border-l border-gray-700/30 align-top">
                                                        {hasOrders ? (
                                                            <div className="flex flex-col gap-1">
                                                                {row.matchingOrders.map((o, i) => (
                                                                    <div key={i}>
                                                                        {o.stopPrice || o.stop_price || "-"}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-600">-</span>
                                                        )}
                                                    </td>
                                                    <td className="py-2.5 px-4 text-center text-gray-300 font-medium border-l border-gray-700/30 align-top">
                                                        {hasOrders ? (
                                                            <div className="flex flex-col gap-1">
                                                                {row.matchingOrders.map((o, i) => (
                                                                    <div key={i}>
                                                                        {o.price || o.limitPrice || o.limit_price || "-"}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-600">-</span>
                                                        )}
                                                    </td>
                                                    <td className="py-2.5 px-4 text-center border-l border-gray-700/50 align-middle">
                                                        {hasOrders ? (
                                                            <div className="flex flex-row items-center justify-center gap-2">
                                                                <div className="flex flex-col gap-1 items-center">
                                                                    {row.matchingOrders.map((o, i) => (
                                                                        <div key={i} className="h-[20px] flex items-center gap-1">
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    openModifyModal([o]);
                                                                                }}
                                                                                className="text-blue-500/70 hover:text-blue-400 hover:bg-blue-500/10 p-1.5 rounded transition-all group border border-transparent hover:border-blue-500/30"
                                                                                title={`Modify Order ${o.order_id || o.orderId}`}
                                                                            >
                                                                                <Pencil className="h-3.5 w-3.5" />
                                                                            </button>
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    openCancelModal([o]);
                                                                                }}
                                                                                className="text-red-500/70 hover:text-red-400 hover:bg-red-500/10 p-1.5 rounded transition-all group border border-transparent hover:border-red-500/30"
                                                                                title={`Cancel Order ${o.order_id || o.orderId}`}
                                                                            >
                                                                                <svg
                                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                                    className="h-4 w-4"
                                                                                    fill="none"
                                                                                    viewBox="0 0 24 24"
                                                                                    stroke="currentColor"
                                                                                >
                                                                                    <path
                                                                                        strokeLinecap="round"
                                                                                        strokeLinejoin="round"
                                                                                        strokeWidth={1.5}
                                                                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                                                    />
                                                                                </svg>
                                                                            </button>
                                                                        </div>
                                                                    ))}
                                                                </div>

                                                                {row.matchingOrders.length > 1 && (
                                                                    <div className="flex flex-col gap-2">
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                openCancelModal(row.matchingOrders);
                                                                            }}
                                                                            className="text-[9px] font-bold text-red-400 hover:text-white bg-red-950/30 hover:bg-red-600 border border-red-900/50 hover:border-red-500 px-2 py-1 rounded transition-all whitespace-nowrap shadow-sm"
                                                                            title="Cancel All Matching Orders"
                                                                        >
                                                                            Cancel All
                                                                        </button>
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                openModifyModal(row.matchingOrders);
                                                                            }}
                                                                            className="text-[9px] font-bold text-blue-400 hover:text-white bg-blue-950/30 hover:bg-blue-600 border border-blue-900/50 hover:border-blue-500 px-2 py-1 rounded transition-all whitespace-nowrap shadow-sm"
                                                                            title="Modify All Matching Orders"
                                                                        >
                                                                            Modify All
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-600">-</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            </div>

            <BulkModifyModal 
                isOpen={modifyModalOpen}
                onClose={() => setModifyModalOpen(false)}
                orders={ordersToModify}
                onSuccess={() => {
                    setSelectedOrderIds(new Set());
                }}
            />
        </>
    );
}
